import { FC, useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui';
import api from '@/utils/api';
import { X } from 'lucide-react';
import { step3Schema, type Step3Data as FormData } from '@/validations';

interface Step2CompetitorsProps {
  initialData?: { competitors?: Array<{ id: number; name: string }> };
  onNext: (data: FormData) => void;
  onBack?: () => void;
  isSubmitting?: boolean;
  skipReset?: boolean;
}

export const Step2Competitors: FC<Step2CompetitorsProps> = ({
  initialData,
  onNext,
  onBack,
  isSubmitting = false,
  skipReset = false,
}) => {
  const [options, setOptions] = useState<Array<{ id: number; name: string; website?: string }>>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<Array<{ id: number; name: string }>>(
    initialData?.competitors || []
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(step3Schema),
    defaultValues: {
      competitorIds: initialData?.competitors?.map((c) => c.id) || [],
    },
  });

  // Reset form when initialData changes, but only if it's actually different
  const prevInitialDataRef = useRef(initialData);
  useEffect(() => {
    if (skipReset) return;
    
    const prevIds = prevInitialDataRef.current?.competitors?.map((c) => c.id).sort().join(',') || '';
    const currentIds = initialData?.competitors?.map((c) => c.id).sort().join(',') || '';
    
    if (prevIds === currentIds && prevInitialDataRef.current) return;
    
    prevInitialDataRef.current = initialData;
    const competitorIds = initialData?.competitors?.map((c) => c.id) || [];
    reset({ competitorIds });
    setSelectedCompanies(initialData?.competitors || []);
  }, [initialData, reset, skipReset]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue && inputValue.length >= 2) {
        const performSearch = async () => {
          setLoading(true);
          try {
            const response = await api.get(`/wizard/companies?search=${encodeURIComponent(inputValue)}`);
            setOptions(response.data || []);
          } catch (error) {
            console.error('Error searching companies:', error);
            setOptions([]);
          } finally {
            setLoading(false);
          }
        };
        performSearch();
      } else if (inputValue.length < 2) {
        setOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const addCompetitor = (company: { id: number; name: string }) => {
    if (!selectedCompanies.find((c) => c.id === company.id)) {
      const newSelected = [...selectedCompanies, company];
      setSelectedCompanies(newSelected);
      setValue('competitorIds', newSelected.map((c) => c.id));
      setInputValue('');
      setOptions([]);
    }
  };

  const removeCompetitor = (id: number) => {
    const newSelected = selectedCompanies.filter((c) => c.id !== id);
    setSelectedCompanies(newSelected);
    setValue('competitorIds', newSelected.map((c) => c.id));
  };

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-6">
      {/* Header */}
      {onBack && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Competitor Selection</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Select your main competitors to monitor
          </p>
        </div>
      )}

      {/* Search Input */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">
            Competitors <span className="text-red-500">*</span>
          </label>
        </div>
        <Controller
          name="competitorIds"
          control={control}
          render={() => (
            <div>
              <div className="relative">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Start typing to search..."
                  className="w-full h-9 px-3 text-sm text-slate-900 border border-slate-200 rounded-lg 
                    focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-shadow"
                />
                {loading && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin h-4 w-4 border-2 border-slate-300 border-t-transparent rounded-full" />
                  </div>
                )}
                {options.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {options.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => addCompetitor(option)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                      >
                        <div className="text-sm font-medium text-slate-900">{option.name}</div>
                        {option.website && (
                          <div className="text-xs text-slate-500">{option.website}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Competitors */}
              {selectedCompanies.length > 0 && (
                <div className="mt-3 space-y-2">
                  {selectedCompanies.map((company) => (
                    <div
                      key={company.id}
                      className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg"
                    >
                      <span className="text-sm font-medium text-slate-900">{company.name}</span>
                      <button
                        type="button"
                        onClick={() => removeCompetitor(company.id)}
                        className="text-slate-400 hover:text-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        />
        {errors.competitorIds && (
          <p className="text-xs text-red-600">{errors.competitorIds.message}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200 justify-end">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};
